export enum StringDateFormat {
  /** e.g. `2024-03-16T14:30:00` */
  ISO_LOCAL_DATE_TIME = "ISO_LOCAL_DATE_TIME",
  /** e.g. `2024-03-16` */
  ISO_DATE = "ISO_DATE",
  /** e.g. `2024-03-16T14:30:00Z` or `2024-03-16T14:30:00+01:00` */
  ISO_INSTANT = "ISO_INSTANT",
}

const FORMAT_PATTERNS: Record<StringDateFormat, RegExp> = {
  [StringDateFormat.ISO_LOCAL_DATE_TIME]: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/,
  [StringDateFormat.ISO_DATE]: /^\d{4}-\d{2}-\d{2}$/,
  [StringDateFormat.ISO_INSTANT]: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/,
};

/**
 * A wrapper around a string representing a date or time.
 *
 * Used to avoid losing information about the original data when converting incoming payloads dates,
 * which can be of varying formats or precision (local / instant).
 */
export class StringDate {
  private constructor(
    private readonly str: string,
    private readonly _format: StringDateFormat,
  ) {}

  static fromIsoLocalDateTime(raw: string): StringDate {
    return StringDate.create(raw, StringDateFormat.ISO_LOCAL_DATE_TIME);
  }

  static fromIsoDate(raw: string): StringDate {
    return StringDate.create(raw, StringDateFormat.ISO_DATE);
  }

  static fromIsoInstant(raw: string): StringDate {
    return StringDate.create(raw, StringDateFormat.ISO_INSTANT);
  }

  get format(): StringDateFormat {
    return this._format;
  }

  toDate(): Date {
    return new Date(this.str);
  }

  toString(): string {
    return this.str;
  }

  private static create(raw: string, format: StringDateFormat): StringDate {
    if (!FORMAT_PATTERNS[format].test(raw)) {
      console.warn(`[StringDate] "${raw}" does not match expected format ${format}`);
    }
    return new StringDate(raw, format);
  }
}
